import { REGEX } from '@shared/constants/regex';

/**
 * The one WebSocket to the API (plan §10.3). It carries small hint events; screens react by
 * refetching, so a dropped connection only costs freshness. Reconnects with backoff, pings
 * every 25 s (the server drops silent sockets after 70 s) and reports auth closes:
 * 4401 = token expired or revoked (refresh, then reconnect), 4403 = account deactivated.
 */
export interface RealtimeEvent {
  type: string;
  [key: string]: unknown;
}

export interface SocketHandlers {
  onEvent: (event: RealtimeEvent) => void;
  /** The server rejected the token; refresh it (a new token triggers a reconnect). */
  onAuthExpired: () => void;
  onAccountBlocked: () => void;
  onStatus?: (connected: boolean) => void;
}

export const PING_MS = 25_000;
const MAX_BACKOFF_MS = 30_000;

/** `/api/v1` or `https://api.example.com/api/v1` → `ws(s)://…/api/v1/ws?token=…` */
export function socketUrl(apiBaseUrl: string, token: string, origin = window.location.origin): string {
  const url = new URL(`${apiBaseUrl.replace(REGEX.trailingSlash, '')}/ws`, origin);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  url.searchParams.set('token', token);
  return url.toString();
}

export class RealtimeSocket {
  private ws: WebSocket | null = null;
  private ping: ReturnType<typeof setInterval> | null = null;
  private retry: ReturnType<typeof setTimeout> | null = null;
  private attempts = 0;
  private stopped = true;

  private readonly url: () => string;
  private readonly handlers: SocketHandlers;

  constructor(url: () => string, handlers: SocketHandlers) {
    this.url = url;
    this.handlers = handlers;
  }

  start(): void {
    this.stopped = false;
    this.open();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimers();
    const ws = this.ws;
    this.ws = null;
    if (ws && ws.readyState <= WebSocket.OPEN) ws.close(1000);
  }

  private open(): void {
    const ws = new WebSocket(this.url());
    this.ws = ws;
    ws.onopen = () => {
      this.attempts = 0;
      this.handlers.onStatus?.(true);
      this.ping = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ type: 'ping' }));
      }, PING_MS);
    };
    ws.onmessage = (msg: MessageEvent<string>) => {
      let event: RealtimeEvent;
      try {
        event = JSON.parse(msg.data) as RealtimeEvent;
      } catch {
        return;
      }
      if (event.type !== 'pong' && event.type !== 'hello') this.handlers.onEvent(event);
    };
    ws.onclose = (close: CloseEvent) => {
      if (this.ws !== ws) return; // replaced or stopped on purpose
      this.ws = null;
      this.clearTimers();
      this.handlers.onStatus?.(false);
      if (this.stopped) return;
      if (close.code === 4401) {
        this.handlers.onAuthExpired();
        return;
      }
      if (close.code === 4403) {
        this.handlers.onAccountBlocked();
        return;
      }
      const delay = Math.min(MAX_BACKOFF_MS, 1000 * 2 ** this.attempts);
      this.attempts += 1;
      this.retry = setTimeout(() => {
        if (!this.stopped) this.open();
      }, delay);
    };
  }

  private clearTimers(): void {
    if (this.ping) clearInterval(this.ping);
    if (this.retry) clearTimeout(this.retry);
    this.ping = null;
    this.retry = null;
  }
}
